/**
 * continuity.ts (JARVIS-PLATFORM-ORB Phase 3, decision C)
 *
 * Conversation + envelope continuity across page hops in today's MPA, and the
 * typed navigation command. Everything here is client-side and reversible:
 *
 * - OPERATOR SESSION: one id per browser session (sessionStorage), so the
 *   persisted panel state is keyed to the operator's session rather than a
 *   global slot. A future SPA shell keeps the same key working unchanged.
 * - ENVELOPE TRAIL: the sections the operator has visited this session, deduped
 *   and capped. The trail is folded into platform_context as a short
 *   `recent_sections` string, which the ask backend's PAGE CONTEXT block
 *   (Phase 2) renders verbatim - page memory with NO server change.
 * - TYPED NAVIGATION: "take me to billing" style commands resolve against the
 *   same allowlisted section list the generative registry uses, and execute
 *   directly (the phrasing IS the confirmation). Generative navigate buttons
 *   confirm on tap instead; both paths end in the host router.
 *
 * Live voice across a full page navigation stays deferred (decision C): an MPA
 * reload destroys the JS context. The SPA-shell join is the persistent mount in
 * entry.tsx plus this module's session-keyed state - a shell that keeps the
 * dock node alive gets seamless voice continuity through the same key, with no
 * migration.
 *
 * No em-dashes; no internal codename in any string this module produces.
 */

/* Same allowlist as the generative registry and the ask backend.
   LEADS GAP (operator repro 2026-08-27): "open leads" was the one section
   command that did nothing while every other section moved the screen. Two
   vocabularies had to disagree for that, and both did:
     1. the server classifier's jarvis_intents config carried a row for every
        nav section EXCEPT leads (the page was itself unreachable until
        2026-08-20, and the intent seed predates it), so the classifier
        returned a miss;
     2. this allowlist - the deterministic client-side fallback the miss falls
        through to - was seeded from the same era and also had no 'leads'.
   Either one alone would have caught it. The section list here is now the
   config/verticals/isp.js `sections` keys verbatim; a section that ships in
   the nav and is missing here is the bug, not a policy. */
export const NAV_SECTIONS = ['dashboard', 'customers', 'leads', 'network', 'appointments', 'costs', 'discovery', 'tickets', 'billing', 'resolve', 'settings'];

const SESSION_KEY = 'orbi_operator_session_v1';

/** One id per operator browser session. Persisted state is keyed to it so a
 *  future authenticated shell can swap in a real operator id transparently. */
export function operatorSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return 'anon';
  }
}

export interface TrailEntry {
  /** section key, e.g. "customers" */
  s: string;
  /** epoch ms when the operator was there */
  t: number;
}

const TRAIL_CAP = 8;

/** Append the current section to the trail; consecutive duplicates collapse so
 *  a page refresh never fakes a hop. Returns a new array, capped. */
export function pushTrail(trail: TrailEntry[], section: string | null | undefined): TrailEntry[] {
  const s = (section || '').trim();
  if (!s) return trail;
  const last = trail[trail.length - 1];
  if (last && last.s === s) return trail;
  return [...trail, { s, t: Date.now() }].slice(-TRAIL_CAP);
}

/** The previous sections (excluding the current one), most recent last, as a
 *  short plain string for the PAGE CONTEXT block: "network > customers". Null
 *  when there is no history worth mentioning. */
export function trailSummary(trail: TrailEntry[], currentSection: string | null | undefined): string | null {
  const cur = (currentSection || '').trim();
  // drop the trailing current-section entry; what remains is the path that led here
  const path = trail.map((e) => e.s);
  if (path.length && cur && path[path.length - 1] === cur) path.pop();
  if (path.length === 0) return null;
  return path.slice(-3).join(' > ');
}

export interface NavCommand {
  section: string;
  client_slug: string;
  client_name?: string;
}

/* VERB PARITY (same 2026-08-27 repro): the jarvis_intents rows have always
   understood BOTH families - "open/show me the X" and "take me / go to X" -
   but this detector only ever knew the second. So the client-side fallback
   could not rescue a classifier miss phrased the most natural way, which is
   exactly how the operator says it. The allowlist above is what keeps the
   wider verb set safe: anything whose tail is not an allowlisted section
   still returns null and falls through to the ask. */
const NAV_VERB_RE = /^(?:take\s+me|go|navigate|switch|jump|head|open|show|pull\s+up|bring\s+up)\s+(?:me\s+)?(?:back\s+)?(?:over\s+)?(?:to\s+)?(?:the\s+)?(.+)$/i;

/** Detect an explicit typed navigation command ("take me to billing", "go to
 *  customers for conduct connect"). Returns null unless the target resolves to
 *  an allowlisted section, so ordinary sentences fall through to the ask. */
export function detectNavCommand(
  text: string,
  clients: Array<{ slug: string; name: string }> = []
): NavCommand | null {
  const m = (text || '').trim().match(NAV_VERB_RE);
  if (!m || !m[1]) return null;
  let rest = m[1].trim().replace(/[?.!]+$/, '').replace(/\s+(page|screen|view|section|tab)$/i, '').trim();
  // optional client qualifier: "billing for conduct connect" / "billing on tnt"
  let clientSlug = '';
  let clientName: string | undefined;
  const forM = rest.match(/^(.*?)\s+(?:for|on|at|in)\s+(.+)$/i);
  let sectionWord = rest;
  if (forM) {
    const cand = forM[2].trim().toLowerCase();
    const hit = clients.find((c) => c.slug.toLowerCase() === cand || c.name.toLowerCase() === cand || c.slug.toLowerCase() === cand.replace(/\s+/g, '-'));
    if (hit) {
      sectionWord = forM[1].trim();
      clientSlug = hit.slug;
      clientName = hit.name;
    }
  }
  const section = sectionWord.toLowerCase();
  if (!NAV_SECTIONS.includes(section)) return null;
  return { section, client_slug: clientSlug, ...(clientName ? { client_name: clientName } : {}) };
}
